import React, { useEffect, useRef, useState, useCallback } from "react";
import { resolveImage } from "../../../shared/services/api";
import { ChevronLeft, ChevronRight, X, Home, Maximize2 } from "lucide-react";

/**
 * v2.70 Phase 4 — ImageGallery (v2.70.1 mobile-fit hardening)
 *
 * Smart responsive image handling:
 *  - Aspect ratio: 4:3 on mobile, 16:10 on desktop.
 *  - Foreground image uses `object-contain` so NO portrait / landscape /
 *    square image ever overflows or gets badly cropped on mobile.
 *  - Behind it, a blurred copy of the same image fills the frame using
 *    `object-cover` — Instagram-style backdrop so the container never
 *    looks empty when contain-mode leaves bars.
 *  - Skeleton shimmer until the image's `onLoad` fires, preventing layout
 *    flash on slow networks.
 *  - Lightbox always shows full image with `object-contain` on a black canvas.
 *  - Touch/mouse swipe + arrow nav + keyboard ← → + Escape close preserved.
 *
 * Props:
 *  - images: array of paths (may be empty)
 *  - title: alt text
 *  - sold: bool — apply grayscale tint
 */
export default function ImageGallery({ images = [], title = "", sold = false }) {
  const list = images.length ? images : [null];
  const [active, setActive] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [loadedSet, setLoadedSet] = useState(() => new Set());
  const trackRef = useRef(null);
  const dragStart = useRef(null);

  const next = useCallback(() => setActive((i) => (i + 1) % list.length), [list.length]);
  const prev = useCallback(() => setActive((i) => (i - 1 + list.length) % list.length), [list.length]);

  // Keyboard nav (works both for main view + lightbox)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape" && showLightbox) setShowLightbox(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, showLightbox]);

  // Touch / mouse drag for swipe
  const handlePointerDown = (e) => { dragStart.current = e.clientX ?? e.touches?.[0]?.clientX ?? null; };
  const handlePointerUp = (e) => {
    if (dragStart.current == null) return;
    const endX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? null;
    if (endX == null) return;
    const dx = endX - dragStart.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next(); else prev();
    }
    dragStart.current = null;
  };

  // v2.71.80 — Detail-page gallery uses a 1200w variant for the main image
  // (HD on retina up to 600 CSS px) and 200w for thumbnails. WebP-aware via
  // backend Accept negotiation. Cuts main-image payload from ~3 MB → ~80 KB.
  const mainImg = list[active] ? resolveImage(list[active], { w: 1200 }) : null;
  const isLoaded = mainImg && loadedSet.has(mainImg);
  const [naturalRatios, setNaturalRatios] = useState({});
  const naturalRatio = mainImg ? naturalRatios[mainImg] : null;
  // v2.70.2 — smart object-fit:
  //   • Tall/portrait images (ratio < 0.9) → object-contain on every breakpoint (no top-half cuts)
  //   • Landscape/square images on mobile → object-contain too (avoids edge cropping on narrow screens),
  //     switches to object-cover from `sm:` upward where the 16:10 frame is wide enough for cover to look natural.
  //   • Unknown ratio (still loading) → object-contain everywhere (safest default)
  const isPortrait = naturalRatio !== null && naturalRatio !== undefined && naturalRatio < 0.9;
  const fitClass = isPortrait ? "object-contain" : "object-contain sm:object-cover";
  const markLoaded = (src) => setLoadedSet((s) => (s.has(src) ? s : new Set(s).add(src)));
  const recordRatio = (src, w, h) => {
    if (!w || !h) return;
    const r = w / h;
    setNaturalRatios((s) => (s[src] ? s : { ...s, [src]: r }));
  };

  return (
    <>
      <div className="relative" data-testid="property-gallery">
        {/* Main image — responsive aspect + blurred backdrop + object-contain foreground */}
        <div
          ref={trackRef}
          onMouseDown={handlePointerDown}
          onMouseUp={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchEnd={handlePointerUp}
          className={`relative aspect-[4/3] sm:aspect-[16/10] bg-[#09090B] rounded-sm overflow-hidden border border-[#E4E4E7] select-none touch-pan-y ${mainImg ? "cursor-zoom-in" : ""} ${sold ? "grayscale opacity-80" : ""}`}
          onClick={() => mainImg && setShowLightbox(true)}
          data-testid="gallery-main-image"
        >
          {mainImg ? (
            <>
              {/* Skeleton shimmer (visible until image loads) */}
              {!isLoaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#E4E4E7] via-[#F4F4F5] to-[#E4E4E7] bg-[length:200%_100%] animate-shimmer" data-testid="gallery-skeleton" />
              )}
              {/* Blurred backdrop — fills the container with object-cover so contain-mode bars look polished */}
              <img
                aria-hidden="true"
                src={mainImg}
                alt=""
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 pointer-events-none"
              />
              {/* Foreground image — smart object-fit per natural aspect ratio */}
              <img
                loading="lazy"
                src={mainImg}
                alt={title}
                onLoad={(e) => { markLoaded(mainImg); recordRatio(mainImg, e.target.naturalWidth, e.target.naturalHeight); }}
                onError={() => markLoaded(mainImg)}
                className={`relative w-full h-full ${fitClass} pointer-events-none transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
                data-testid="gallery-foreground-image"
                data-orientation={isPortrait ? "portrait" : "landscape"}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#A1A1AA] bg-[#F4F4F5]">
              <Home size={80} strokeWidth={1} />
            </div>
          )}

          {/* Arrow nav */}
          {list.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 hover:bg-white shadow-md backdrop-blur-sm flex items-center justify-center transition-all hover:scale-105 z-10"
                data-testid="gallery-prev"
                aria-label="Previous image"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 hover:bg-white shadow-md backdrop-blur-sm flex items-center justify-center transition-all hover:scale-105 z-10"
                data-testid="gallery-next"
                aria-label="Next image"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {/* Counter + fullscreen hint */}
          {mainImg && (
            <div className="absolute bottom-3 right-3 inline-flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white text-[11px] font-mono-data px-2.5 py-1 rounded-full z-10">
              <Maximize2 size={11} />
              <span>{active + 1} / {list.length}</span>
            </div>
          )}
        </div>

        {/* Thumbnails strip */}
        {list.length > 1 && (
          <div className="mt-3 flex flex-nowrap gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x scroll-smooth [-webkit-overflow-scrolling:touch]" data-testid="gallery-thumbs">
            {list.map((src, i) => (
              <button
                key={`${src || "empty"}-${i}`}
                onClick={() => setActive(i)}
                className={`shrink-0 snap-start w-16 sm:w-20 aspect-[4/3] border-2 rounded-sm overflow-hidden transition-all bg-[#F4F4F5] ${
                  i === active ? "border-[#0055FF] shadow-sm" : "border-transparent opacity-60 hover:opacity-100"
                }`}
                data-testid={`thumb-${i}`}
              >
                {src ? <img loading="lazy" src={resolveImage(src, { w: 200 })} alt="" className="w-full h-full object-cover" /> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {showLightbox && mainImg && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setShowLightbox(false)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lightbox-close"
            aria-label="Close fullscreen"
          >
            <X size={20} />
          </button>
          {list.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="lightbox-next"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
          <img
            src={mainImg}
            alt={title}
            className="max-w-[95vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-[12px] font-mono-data bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
            {active + 1} / {list.length}
          </div>
        </div>
      )}
    </>
  );
}
